import { Color } from 'chart.js';
import { Result } from 'src/app/common/chartUtils';
import {
  attackToDisplayName,
  owaspLcncToDisplayName,
  owaspLlmToDisplayName,
  owaspAsiToDisplayName,
  owaspNhiToDisplayName,
} from 'src/app/common/violationRulesUtils';
import { AggregationDict, RawAggregationCategorySum, RulesAmounts } from './aggregations.model';
import { ISelectedFilters } from '../filters/selectedFilters.interface';

export type SeriesTransformFunc = (series: PointInTime[]) => PointInTime[];

export const noTransform: SeriesTransformFunc = (series) => series;

export interface DataTransform {
  id: string;
  color: Color;
  seriesTransformationFunc?: SeriesTransformFunc;
  pageLinkName?: string;
  selectedFilters?: ISelectedFilters;
}

export type TypeSafeDataTransform<T> = Pick<
  DataTransform,
  'color' | 'seriesTransformationFunc' | 'pageLinkName' | 'selectedFilters'
> & {
  id: T[keyof T];
};

export const toDataTransform = <T>(transform: TypeSafeDataTransform<T>) => transform as DataTransform;

export interface AggregationTrendSeries {
  name: string;
  data: PointInTime[];
  transform: DataTransform;
}

export type PointInTime = { x: Date; y: number; tooltip: string };

// TODO: Remove once we remove the 'data' field on the backend. Replace with AggregationCategorySumDetails
export type RawAggregationCategorySumDetails = Pick<
  AggregationCategorySumDetails,
  'categoryName' | 'categoryLinkValue'
> & {
  amountPerSubCategory: { data: AggregationDict };
};

export interface AggregationCategorySumDetails {
  categoryName: string;
  categoryLinkValue: string;
  amountPerSubCategory: AggregationDict;
}

export interface AggregationCategorySumWithExtraInfo extends AggregationCategorySumDetails {
  displayName: string;
}

export interface ResourceViolations {
  id: string;
  name: string;
  low: number;
  medium: number;
  high: number;
}

export interface IChartData {
  name: string;
  value: number;
  itemStyle: { color: string };
  pageLinkName?: string;
  selectedFilters?: ISelectedFilters;
}

export interface AggregationPieChartDetails {
  data: IChartData[];
}

export interface AggregationRulesDetails {
  rulesAmount: number;
  rulesFailedAmount: number;
  rulesPassedAmount: number;
}

export const toResourceViolations =
  (displayNameAdapter: (id: string) => string = (id) => id) =>
  (entry: RawAggregationCategorySum) => ({
    id: entry.categoryName,
    name: displayNameAdapter(entry.categoryName),
    low: entry.amountPerSubCategory.data['low'] ?? 0,
    medium: entry.amountPerSubCategory.data['medium'] ?? 0,
    high: entry.amountPerSubCategory.data['high'] ?? 0,
  });

const addAllCategories = (categories: RawAggregationCategorySum[], source: Record<string, string>) => {
  const existingCategories = categories.toSetBy((category) => category.categoryName);
  const missingCategories = Object.keys(source)
    .filter((key) => !existingCategories.has(key))
    .map(
      (key) =>
        ({
          categoryName: key,
          amountPerSubCategory: { data: {} },
        }) as RawAggregationCategorySum
    );
  categories.addAll(...missingCategories);
};

export const addAllOwaspCategories = (categories: RawAggregationCategorySum[]) =>
  addAllCategories(categories, owaspLcncToDisplayName);

export const addAllOwaspLlmCategories = (categories: RawAggregationCategorySum[]) =>
  addAllCategories(categories, owaspLlmToDisplayName);

export const addAllMitreCategories = (categories: RawAggregationCategorySum[]) =>
  addAllCategories(categories, attackToDisplayName);

export const addAllOwaspAsiCategories = (categories: RawAggregationCategorySum[]) =>
  addAllCategories(categories, owaspAsiToDisplayName);

export const addAllOwaspNhiCategories = (categories: RawAggregationCategorySum[]) =>
  addAllCategories(categories, owaspNhiToDisplayName);

export const toOwaspDisplayName = (id: string) => owaspLcncToDisplayName[id];

export const toOwaspLlmDisplayName = (id: string) => owaspLlmToDisplayName[id];

export const toOwaspAsiDisplayName = (id: string) => owaspAsiToDisplayName[id];

export const toOwaspNhiDisplayName = (id: string) => owaspNhiToDisplayName[id];

export const toMitreDisplayName = (id: string) => attackToDisplayName[id];

export const byDisplayName = (a: ResourceViolations, b: ResourceViolations) => a.name.localeCompare(b.name);

export const violationCycleTimeMedian = (entries: Record<number, number>) =>
  Object.entries(entries)
    .map(([key, value]) => [Number(key) + 1, value]) // Number of days to close is 1-based (minimum of 1, never zero)
    .flatMap(([days, repetitions]) => Array(repetitions).fill(days))
    .median();

// To remove once i1727 is resolved and the data is received from the backend in the proper format.
export function rulesAmountTempAdapter(data: RulesAmounts) {
  if (data === undefined) {
    return { [Result.passed]: 0, [Result.failed]: 0 };
  }

  const failedRules = data['failedRules']?.length ?? 0;
  const totalRules = data['totalRules']?.length ?? 0;

  return {
    [Result.passed]: totalRules - failedRules,
    [Result.failed]: failedRules,
  };
}
